import React, {
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";
import ReactDOM from "react-dom";
import {
  Box,
  Typography,
  Button,
  Stack,
  IconButton,
  Divider,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import "./ErrorDialog.css";

const ErrorDialog = forwardRef(function ErrorDialog(props, ref) {
  const dialogRef = useRef(null);

  useImperativeHandle(ref, () => ({
    openDialog() {
      dialogRef.current?.showModal();
    },
    closeDialog() {
      dialogRef.current?.close();
    },
  }));

  // Handle native ESC / backdrop close → notify parent
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handleClose = () => props?.setIsError?.(false);
    dialog.addEventListener("close", handleClose);
    return () => dialog.removeEventListener("close", handleClose);
  }, [props]);

  const handleClose = () => {
    dialogRef.current?.close();
    props?.setIsError?.(false);
  };

  const dialogContent = (
    <dialog
      ref={dialogRef}
      className="custom-dialog"
      style={{
        border: "none",
        borderRadius: 16,
        padding: 0,
        maxWidth: 420,
        width: "92vw",
        boxShadow: "0 24px 60px rgba(0,0,0,0.25)",
        overflow: "hidden",
      }}
    >
      {/* Header strip with warning icon */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          p: "22px 22px 16px 22px",
          position: "relative",
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            bgcolor: "#FEF2F2",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <ErrorOutlineRoundedIcon sx={{ fontSize: 26, color: "#DC2626" }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 700,
              color: "#111827",
              lineHeight: 1.3,
              pr: 3,
            }}
          >
            {props.error || "Something went wrong!"}
          </Typography>
          <Typography
            sx={{
              fontSize: 13,
              color: "#6B7280",
              lineHeight: 1.5,
              mt: 0.75,
            }}
          >
            {props.message || "Please try again later."}
          </Typography>
        </Box>

        <IconButton
          onClick={handleClose}
          size="small"
          aria-label="close"
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            color: "#9CA3AF",
            "&:hover": { color: "#374151", bgcolor: "#F3F4F6" },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <Divider sx={{ borderColor: "#F3F4F6" }} />

      {/* Footer action */}
      <Stack
        direction="row"
        justifyContent="flex-end"
        sx={{ p: "14px 22px 18px 22px" }}
      >
        <Button
          onClick={handleClose}
          variant="contained"
          disableElevation
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: 13,
            borderRadius: 2,
            px: 2.5,
            py: 0.9,
            bgcolor: "#DC2626",
            boxShadow: "0 3px 10px rgba(220,38,38,.25)",
            "&:hover": {
              bgcolor: "#B91C1C",
              boxShadow: "0 5px 14px rgba(220,38,38,.35)",
            },
          }}
        >
          Close
        </Button>
      </Stack>
    </dialog>
  );

  return ReactDOM.createPortal(dialogContent, document.getElementById("modal"));
});

export default ErrorDialog;
